import { useEffect, useState } from "react";
import api from "../../lib/api";
import { eur } from "../../lib/constants";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AlertTriangle, Calculator } from "lucide-react";
import { COST_TYPES, label, costText, discountText, today } from "./meta";

const Line = ({ l, v, strong, testid }) => (
  <div className={`flex justify-between gap-3 py-1 ${strong ? "font-head font-extrabold text-lg border-t border-border pt-2 mt-1" : "text-sm"}`}><span>{l}</span><span data-testid={testid}>{v}</span></div>
);

export default function Simulator({ sup, discounts, costs }) {
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");
  const [disc, setDisc] = useState("none");
  const [manual, setManual] = useState("");
  const [sel, setSel] = useState({});
  const [belowMin, setBelowMin] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const active = discounts.filter((d) => !d.expiry || d.expiry >= today());

  useEffect(() => {
    if (!price) { setRes(null); setErr(""); return; }
    const t = setTimeout(async () => {
      try {
        const body = { list_price: price, quantity: qty || 1, discount_id: disc !== "none" && disc !== "manual" ? disc : "", manual_discount: disc === "manual" ? manual : "",
          costs: Object.entries(sel).filter(([, v]) => v.on).map(([cost_id, v]) => ({ cost_id, units: v.units, value: v.value })), apply_below_min: belowMin };
        const { data } = await api.post(`/suppliers/${sup.id}/simulate`, body); setRes(data); setErr("");
      } catch (e) { setErr(e?.response?.data?.detail || "Calcolo non riuscito"); }
    }, 300);
    return () => clearTimeout(t);
  }, [price, qty, disc, manual, sel, belowMin, sup.id]);

  const toggle = (id, patch) => setSel((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="sec-simulatore">
      <div className="card-soft p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Prezzo di listino unitario €</Label><Input data-testid="sim-price" type="number" step="any" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
          <div><Label className="text-xs">Quantità (articoli)</Label><Input data-testid="sim-qty" type="number" step="any" min="1" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        </div>
        <div><Label className="text-xs">Sconto</Label>
          <Select value={disc} onValueChange={setDisc}>
            <SelectTrigger data-testid="sim-discount"><SelectValue /></SelectTrigger>
            <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
              <SelectItem value="none">Nessuno sconto</SelectItem>
              {active.map((d) => <SelectItem key={d.id} value={d.id}>{d.product} · {discountText(d)}</SelectItem>)}
              <SelectItem value="manual">Inserisco a mano…</SelectItem>
            </SelectContent>
          </Select>
          {disc === "manual" && <Input className="mt-2" data-testid="sim-manual-discount" placeholder="es. 50+10" value={manual} onChange={(e) => setManual(e.target.value)} />}
        </div>
        <div>
          <p className="text-xs font-semibold mb-1">Spese da applicare</p>
          {costs.length === 0 && <p className="text-xs text-muted-foreground">Nessuna spesa registrata per questo fornitore.</p>}
          <div className="space-y-1.5">
            {costs.map((c) => {
              const s = sel[c.id] || {};
              return (
                <div key={c.id} className="rounded-lg bg-slate-50 p-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1" data-testid={`sim-cost-${c.id}`} checked={!!s.on} onChange={(e) => toggle(c.id, { on: e.target.checked })} />
                    <span className="min-w-0"><span className="font-medium">{c.name}</span> <span className="text-xs text-muted-foreground">· {label(COST_TYPES, c.type)} · {costText(c)}</span></span>
                  </label>
                  {s.on && c.basis === "unita" && <Input className="mt-1.5 h-8" data-testid={`sim-units-${c.id}`} type="number" step="any" placeholder={`Quante ${c.unit || "unità"}?`} value={s.units || ""} onChange={(e) => toggle(c.id, { units: e.target.value })} />}
                  {s.on && c.basis === "personalizzato" && <Input className="mt-1.5 h-8" data-testid={`sim-value-${c.id}`} type="number" step="any" placeholder="Importo da applicare €" value={s.value || ""} onChange={(e) => toggle(c.id, { value: e.target.value })} />}
                </div>
              );
            })}
          </div>
          {sup.min_order != null && sup.below_min_fee != null && (
            <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer"><input type="checkbox" data-testid="sim-below-min" checked={belowMin} onChange={(e) => setBelowMin(e.target.checked)} />
              Applica spesa sotto soglia ({eur(sup.below_min_fee)} se l'ordine è sotto {eur(sup.min_order)})</label>
          )}
        </div>
      </div>

      <div className="card-soft p-4 h-fit" data-testid="sim-result">
        <h4 className="font-head font-bold flex items-center gap-2 mb-2"><Calculator size={18} className="text-brand" /> Costo di acquisto</h4>
        {err && <p className="text-sm text-destructive" data-testid="sim-error">{err}</p>}
        {!res && !err && <p className="text-sm text-muted-foreground">Inserisci il prezzo di listino per vedere il calcolo.</p>}
        {res && (<>
          <Line l={`Listino (${eur(res.list_price)} × ${res.quantity})`} v={eur(res.gross)} testid="sim-gross" />
          <Line l={`Sconto ${res.discount_steps.length ? res.discount_steps.join(" + ") + "%" : "—"} (effettivo ${res.effective_discount_pct.toLocaleString("it-IT", { maximumFractionDigits: 2 })}%)`} v={`− ${eur(res.gross - res.net)}`} />
          <Line l="Costo netto dopo lo sconto" v={eur(res.net)} testid="sim-net" />
          {res.lines.map((l, i) => <div key={i} className="flex justify-between gap-3 text-xs text-muted-foreground pl-2"><span>{l.name} · {l.calc}</span><span>{l.amount == null ? "da inserire" : eur(l.amount)}</span></div>)}
          <Line l="Totale spese aggiuntive" v={eur(res.extras_total)} testid="sim-extras" />
          <Line strong l="Costo effettivo (IVA esclusa)" v={eur(res.total)} testid="sim-total" />
          <p className="text-xs text-muted-foreground" data-testid="sim-unit">Costo per articolo: {eur(res.unit_cost)}</p>
          {res.below_min && !belowMin && sup.below_min_fee != null && <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle size={13} /> Ordine sotto la soglia minima: valuta se applicare la spesa.</p>}
          {!res.complete && <p className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid="sim-missing"><AlertTriangle size={13} /> Inserisci il valore per: {res.missing.join(", ")} (non conteggiato).</p>}
          <p className="text-[10px] text-muted-foreground mt-3">Solo simulazione: non modifica preventivi né prezzi di vendita.</p>
        </>)}
      </div>
    </div>
  );
}
